import { Columns3, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { UseTableColumnsResult } from '@/hooks/useTableColumns'

/**
 * The "Columns" control, rendered inside the FIRST header cell of every
 * customizable table by `ResizableTableHead` / `ResizableTh`.
 *
 * It used to sit in a row of its own above the table, which cost a full line of
 * vertical space on every screen in the app and left that line otherwise empty.
 * In the header it costs nothing, and the left edge is the one spot that stays
 * put however the table is scrolled or whichever columns are hidden.
 *
 * Icon-only, because a header cell is not the place for a labelled button — so
 * the accessible name carries the hidden count as well as the label, and stays
 * the thing to locate it by:
 *
 *     getByRole('button', { name: /^columns/i })   // always
 *     getByRole('button', { name: /1 hidden/ })    // with a column hidden
 *
 * Deliberately dumb otherwise: it renders whatever `useTableColumns` gives it
 * and calls back. All the rules about what may be hidden live in
 * `lib/tableColumnPrefs`, so a table cannot get them subtly wrong by wiring
 * this up differently.
 *
 * Locked columns are listed but shown disabled and already checked, rather than
 * omitted. Omitting them made the menu look like it was missing entries — the
 * reader counted the table's columns, counted the menu's, and found a gap with
 * no explanation. Listed-but-disabled says "this one is always on".
 */
export function ColumnsMenu({
  columns,
  label = 'Columns',
}: {
  columns: UseTableColumnsResult
  /** Override for a panel with two tables, so the two menus are distinguishable. */
  label?: string
}) {
  const { all, toggle, reset, customized, hiddenCount } = columns
  // One name, both facts. An icon-only button needs `aria-label`, and that
  // REPLACES name-from-content — so the count has to be folded in here or it
  // stops being discoverable with the popover shut.
  const name = hiddenCount > 0 ? `${label}, ${hiddenCount} hidden` : label

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          aria-label={name}
          title={name}
          // A bordered, filled button, not a ghost. A header cell already has a
          // tinted background and small muted text all over it, and a ghost
          // icon in that company reads as another glyph rather than a control —
          // it was, in practice, invisible. Border, card background and a
          // shadow make it the one thing in the row that is obviously
          // pressable; the icon below supplies the colour.
          className='relative h-8 w-8 shrink-0 border-border bg-background p-0 shadow-sm hover:bg-accent'
        >
          {/*
            The glyph goes orange only as an alarm — something is hidden and the
            reader may be looking at a partial table. With nothing hidden there
            is nothing to raise, so it stays the ordinary foreground colour and
            the DOT carries the all-clear.
          */}
          <Columns3
            className={cn('h-[18px] w-[18px]', hiddenCount > 0 ? 'text-orange-500' : 'text-foreground')}
          />
          {/*
            The dot is the state light and is always lit: green for a complete
            table, orange when columns are hidden. The exact count stays in the
            button's accessible name — at this size a number is unreadable.
          */}
          <span
            aria-hidden
            className={cn(
              'absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-background',
              hiddenCount > 0 ? 'bg-orange-500' : 'bg-green-500',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-64 p-0'>
        <div className='flex items-center justify-between border-b px-3 py-2'>
          <p className='text-sm font-medium'>Show columns</p>
          <Button
            variant='ghost'
            size='sm'
            className='h-7 gap-1 px-2 text-xs'
            disabled={!customized}
            onClick={reset}
            title='Restore the default columns and widths'
          >
            <RotateCcw className='h-3 w-3' /> Reset
          </Button>
        </div>
        <div className='max-h-72 overflow-y-auto p-1'>
          {all.map(({ def, visible }) => (
            <label
              key={def.id}
              className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm ${
                def.locked ? 'opacity-60' : 'cursor-pointer hover:bg-muted'
              }`}
              title={def.locked ? 'Always shown' : undefined}
            >
              <Checkbox
                checked={visible}
                disabled={def.locked}
                onCheckedChange={() => toggle(def.id)}
              />
              <span className='truncate'>{def.header}</span>
            </label>
          ))}
        </div>
        <p className='border-t px-3 py-2 text-xs text-muted-foreground'>
          Saved to your account, so it follows you to any browser. Drag a column
          edge to resize it.
        </p>
      </PopoverContent>
    </Popover>
  )
}
